import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import { 
  Search,
  Filter,
  MapPin,
  Clock,
  CheckCircle,
  Activity,
  PauseCircle,
  Menu,
  X,
  Navigation,
  Trash2,
  Droplets,
  Lightbulb,
  Zap
} from 'lucide-react';

interface Issue {
  id: string;
  title: string;
  category: string;
  status: 'pending' | 'in_progress' | 'resolved';
  priority: 'low' | 'medium' | 'high';
  location: string;
  submittedBy: string;
  submittedAt: string;
  description: string;
  upvotes: number;
  distance?: string;
  coordinates?: { lat: number; lng: number };
}

const mockIssues: Issue[] = [
  {
    id: 'CIV-001',
    title: 'Pothole on Main Street',
    category: 'roads',
    status: 'in_progress',
    priority: 'high',
    location: 'Main Street, Sector 5',
    submittedBy: 'John Doe',
    submittedAt: '2024-01-15T10:30:00Z',
    description: 'Large pothole causing traffic issues and vehicle damage',
    upvotes: 24,
    distance: '0.5 km',
    coordinates: { lat: 28.6139, lng: 77.2090 }
  },
  {
    id: 'CIV-002',
    title: 'Street Light Not Working',
    category: 'streetlight',
    status: 'pending',
    priority: 'medium',
    location: 'Park Avenue, Block B',
    submittedBy: 'Jane Smith',
    submittedAt: '2024-01-14T14:20:00Z',
    description: 'Street light has been off for a week, creating safety concerns',
    upvotes: 12,
    distance: '1.2 km',
    coordinates: { lat: 28.7041, lng: 77.1025 }
  },
  {
    id: 'CIV-003',
    title: 'Water Supply Issue',
    category: 'water',
    status: 'resolved',
    priority: 'high',
    location: 'Garden Road, Phase 2',
    submittedBy: 'Mike Johnson',
    submittedAt: '2024-01-13T09:15:00Z',
    description: 'Irregular water supply timing for past 3 days',
    upvotes: 32,
    distance: '2.1 km',
    coordinates: { lat: 28.5355, lng: 77.3910 }
  },
  {
    id: 'CIV-004',
    title: 'Garbage Collection Missed',
    category: 'garbage',
    status: 'in_progress',
    priority: 'medium',
    location: 'Oak Street, Area 3',
    submittedBy: 'Sarah Williams',
    submittedAt: '2024-01-12T16:45:00Z',
    description: 'Garbage has not been collected for 5 days',
    upvotes: 18,
    distance: '0.8 km',
    coordinates: { lat: 28.6692, lng: 77.4538 }
  },
  {
    id: 'CIV-005',
    title: 'Power Outage',
    category: 'electricity',
    status: 'pending',
    priority: 'high',
    location: 'Elm Street, District 2',
    submittedBy: 'Robert Brown',
    submittedAt: '2024-01-11T11:30:00Z',
    description: 'Complete power outage in the area for past 2 hours',
    upvotes: 42,
    distance: '1.5 km',
    coordinates: { lat: 28.4595, lng: 77.0266 }
  },
  {
    id: 'CIV-006',
    title: 'Broken Drainage System',
    category: 'water',
    status: 'in_progress',
    priority: 'medium',
    location: 'Maple Avenue, Zone 4',
    submittedBy: 'Emily Davis',
    submittedAt: '2024-01-10T08:45:00Z',
    description: 'Drainage overflow causing waterlogging during rains',
    upvotes: 15,
    distance: '0.9 km',
    coordinates: { lat: 28.5672, lng: 77.2100 }
  },
  {
    id: 'CIV-007',
    title: 'Damaged Footpath',
    category: 'roads',
    status: 'pending',
    priority: 'low',
    location: 'Cedar Road, Ward 3',
    submittedBy: 'Michael Wilson',
    submittedAt: '2024-01-09T13:20:00Z',
    description: 'Uneven footpath causing inconvenience to pedestrians',
    upvotes: 8,
    distance: '1.8 km',
    coordinates: { lat: 28.6328, lng: 77.2197 }
  }
];

const categoryIcons: Record<string, React.FC<{ className?: string }>> = {
  roads: Navigation,
  garbage: Trash2,
  water: Droplets,
  streetlight: Lightbulb,
  electricity: Zap
};

const categoryLabels: Record<string, string> = {
  roads: 'Roads & Infrastructure',
  garbage: 'Garbage & Sanitation',
  water: 'Water Supply',
  streetlight: 'Street Lighting',
  electricity: 'Electricity'
};

const BrowseAreaIssues: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { t } = useLanguage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date' | 'upvotes' | 'distance'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [issues, setIssues] = useState<Issue[]>(mockIssues);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (!error) {
      navigate('/login');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-orange-600 bg-orange-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'resolved': return <CheckCircle className="w-4 h-4" />;
      case 'in_progress': return <Activity className="w-4 h-4" />;
      case 'pending': return <PauseCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const filteredIssues = issues.filter(issue => {
    const matchesSearch = issue.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         issue.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         issue.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || issue.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || issue.category === categoryFilter;
    const matchesPriority = priorityFilter === 'all' || issue.priority === priorityFilter;
    
    return matchesSearch && matchesStatus && matchesCategory && matchesPriority;
  });

  const sortedIssues = [...filteredIssues].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'date':
        comparison = new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime();
        break;
      case 'upvotes':
        comparison = b.upvotes - a.upvotes;
        break;
      case 'distance':
        if (a.distance && b.distance) {
          const aDistance = parseFloat(a.distance);
          const bDistance = parseFloat(b.distance);
          comparison = aDistance - bDistance;
        } else {
          comparison = 0;
        }
        break;
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentIssues = sortedIssues.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedIssues.length / itemsPerPage);

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setCategoryFilter('all');
    setPriorityFilter('all');
    setCurrentPage(1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const viewIssueOnMap = (issue: Issue) => {
    navigate('/map', { state: { selectedIssue: issue } });
  };

  // Mobile card component for issues
  const IssueCard: React.FC<{ issue: Issue }> = ({ issue }) => {
    const CategoryIcon = categoryIcons[issue.category] || MapPin;
    
    return (
      <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-white shadow-sm w-full">
        <div className="flex justify-between items-start mb-3">
          <h3 className="font-semibold text-gray-900 text-base">{issue.title}</h3>
          <span className="text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-1 ml-2 flex-shrink-0">#{issue.id}</span>
        </div>
        
        <div className="flex items-center mb-3">
          <CategoryIcon className="w-4 h-4 mr-2 text-gray-500 flex-shrink-0" />
          <span className="text-sm text-gray-700">{categoryLabels[issue.category] || issue.category}</span>
        </div>
        
        <div className="flex flex-wrap gap-2 mb-3">
          <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(issue.status)}`}>
            {getStatusIcon(issue.status)}
            <span className="capitalize">{issue.status.replace('_', ' ')}</span>
          </span>
          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getPriorityColor(issue.priority)}`}>
            <span className="capitalize">{issue.priority}</span>
          </span>
        </div>
        
        <div className="flex items-start mb-3">
          <MapPin className="w-4 h-4 mr-2 mt-0.5 text-gray-500 flex-shrink-0" />
          <span className="text-sm text-gray-700">{issue.location}</span>
        </div>
        
        {issue.distance && (
          <div className="text-sm text-gray-500 mb-3">{issue.distance} away</div>
        )}
        
        <div className="text-sm text-gray-500 mb-3">
          Submitted: {formatDate(issue.submittedAt)}
        </div>
        
        <div className="flex justify-between items-center pt-2 border-t border-gray-100">
          <span className="inline-flex items-center space-x-1 text-sm font-medium text-gray-700">
            <span>👍 {issue.upvotes}</span>
          </span>
          <button
            onClick={() => viewIssueOnMap(issue)}
            className="text-blue-600 hover:text-blue-800 flex items-center text-sm font-medium"
          >
            <MapPin className="w-4 h-4 mr-1" />
            <span>View on Map</span>
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100"
              >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <div className="flex items-center space-x-3">
                <img src="/hero.png" alt="Logo" className="w-8 h-8" />
                <h1 className="text-lg font-bold text-gray-900">Browse Issues</h1>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/report-issue')}
                className="hidden sm:block px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                Report Issue
              </button>
              <button
                onClick={handleSignOut}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t('auth.logout')}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:relative z-30 w-64 h-screen bg-white shadow-lg transition-transform duration-300 ease-in-out top-0 left-0`}>
          <div className="flex justify-between items-center p-4 border-b border-gray-200 lg:hidden">
            <h2 className="text-lg font-semibold text-gray-800">Menu</h2>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <nav className="p-4 space-y-2 overflow-y-auto h-[calc(100vh-4rem)]">
            <button 
              onClick={() => {
                navigate('/dashboard')
                setSidebarOpen(false)
              }}
              className="w-full flex items-center space-x-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <MapPin className="w-5 h-5" />
              <span>Dashboard</span>
            </button>
            <button 
              onClick={() => {
                navigate('/report-issue')
                setSidebarOpen(false)
              }}
              className="w-full flex items-center space-x-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <MapPin className="w-5 h-5" />
              <span>Report New Issue</span>
            </button>
            <button 
              onClick={() => {
                navigate('/my-issues')
                setSidebarOpen(false)
              }}
              className="w-full flex items-center space-x-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <MapPin className="w-5 h-5" />
              <span>My Issues</span>
            </button>
            <button 
              onClick={() => {
                navigate('/browse')
                setSidebarOpen(false)
              }}
              className="w-full flex items-center space-x-3 px-4 py-3 text-left bg-blue-50 text-blue-700 rounded-lg"
            >
              <MapPin className="w-5 h-5" />
              <span>Browse Area Issues</span>
            </button>
            <button 
              onClick={() => {
                navigate('/profile')
                setSidebarOpen(false)
              }}
              className="w-full flex items-center space-x-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <MapPin className="w-5 h-5" />
              <span>Profile</span>
            </button>
          </nav>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-20"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6">
          <div className="max-w-7xl mx-auto space-y-5">
            {/* Page Header */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Browse Issues in Your Area</h2>
                  <p className="text-sm text-gray-600 mt-2">View and track civic issues near you</p>
                </div>
                <div className="flex-shrink-0">
                  <button 
                    onClick={() => navigate('/report-issue')}
                    className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Report Issue
                  </button>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Search */}
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      placeholder="Search issues..."
                      className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
                
                {/* Category Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={categoryFilter}
                    onChange={(e) => {
                      setCategoryFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Categories</option>
                    <option value="roads">Roads</option>
                    <option value="garbage">Garbage</option>
                    <option value="water">Water</option>
                    <option value="streetlight">Streetlight</option>
                    <option value="electricity">Electricity</option>
                  </select>
                </div>
                
                {/* Priority Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                  <select
                    value={priorityFilter}
                    onChange={(e) => {
                      setPriorityFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Priorities</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-5 space-y-3 sm:space-y-0">
                <div className="text-sm text-gray-500">
                  Showing {sortedIssues.length} of {issues.length} issues
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  <button
                    onClick={clearFilters}
                    className="text-sm text-gray-600 hover:text-gray-900"
                  >
                    Clear Filters
                  </button>
                  <div className="flex items-center space-x-3 flex-wrap gap-y-2">
                    <span className="text-sm text-gray-600 whitespace-nowrap">Sort by:</span>
                    <select
                      value={sortBy}
                      onChange={(e) => {
                        setSortBy(e.target.value as any);
                        setCurrentPage(1);
                      }}
                      className="text-sm px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="date">Date</option>
                      <option value="upvotes">Upvotes</option>
                      <option value="distance">Distance</option>
                    </select>
                    <button
                      onClick={() => {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                        setCurrentPage(1);
                      }}
                      className="text-sm px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                    >
                      {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Issues List - Mobile Cards for small screens, Table for larger */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {/* Mobile View - Cards */}
              <div className="block md:hidden">
                <div className="p-4">
                  {currentIssues.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-gray-400 mb-2">
                        <MapPin className="w-12 h-12 mx-auto" />
                      </div>
                      <p className="text-gray-500 text-base">No issues found matching the current filters</p>
                      <button
                        onClick={clearFilters}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        Clear Filters
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {currentIssues.map((issue) => (
                        <IssueCard key={issue.id} issue={issue} />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Desktop View - Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Issue
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                        Category
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                        Priority
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                        Submitted
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Upvotes
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentIssues.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-8 text-center text-gray-500 text-sm">
                          <div className="flex flex-col items-center justify-center">
                            <MapPin className="w-12 h-12 text-gray-300 mb-3" />
                            <p className="text-gray-500 text-base mb-3">No issues found matching the current filters</p>
                            <button
                              onClick={clearFilters}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                            >
                              Clear Filters
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      currentIssues.map((issue) => {
                        const CategoryIcon = categoryIcons[issue.category] || MapPin;
                        return (
                          <tr 
                            key={issue.id} 
                            className="hover:bg-gray-50"
                          >
                            <td className="px-6 py-4">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{issue.title}</div>
                                <div className="text-xs text-gray-500 mt-1">#{issue.id}</div>
                                {issue.distance && (
                                  <div className="text-xs text-gray-400 mt-2">{issue.distance} away</div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                              <div className="flex items-center">
                                <CategoryIcon className="w-5 h-5 mr-2 text-gray-500" />
                                <span className="text-sm text-gray-900">{categoryLabels[issue.category] || issue.category}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(issue.status)}`}>
                                {getStatusIcon(issue.status)}
                                <span className="capitalize">{issue.status.replace('_', ' ')}</span>
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                              <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getPriorityColor(issue.priority)}`}>
                                <span className="capitalize">{issue.priority}</span>
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                              <div className="flex items-start">
                                <MapPin className="w-4 h-4 mr-2 mt-0.5 text-gray-500 flex-shrink-0" />
                                <span className="truncate">{issue.location}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 hidden lg:table-cell">
                              {formatDate(issue.submittedAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <span className="inline-flex items-center space-x-1">
                                <span>{issue.upvotes}</span>
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button
                                onClick={() => viewIssueOnMap(issue)}
                                className="text-blue-600 hover:text-blue-900 flex items-center"
                              >
                                <MapPin className="w-4 h-4 mr-1" />
                                <span className="hidden sm:inline">View on Map</span>
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {sortedIssues.length > itemsPerPage && (
                <div className="bg-white px-4 py-4 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 gap-4">
                  <div className="text-sm text-gray-700">
                    Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(indexOfLastItem, sortedIssues.length)}
                    </span>{' '}
                    of <span className="font-medium">{sortedIssues.length}</span> results
                  </div>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className={`px-3.5 py-2 rounded-md text-sm ${
                        currentPage === 1 
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Prev
                    </button>
                    
                    {/* Show limited page numbers on mobile */}
                    {(() => {
                      const pageNumbers = [];
                      const maxVisiblePages = window.innerWidth < 640 ? 3 : 5;
                      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                      
                      // Adjust if we're near the end
                      if (endPage - startPage + 1 < maxVisiblePages) {
                        startPage = Math.max(1, endPage - maxVisiblePages + 1);
                      }
                      
                      for (let i = startPage; i <= endPage; i++) {
                        pageNumbers.push(i);
                      }
                      
                      return pageNumbers.map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3.5 py-2 rounded-md text-sm ${
                            currentPage === page
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {page}
                        </button>
                      ));
                    })()}
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className={`px-3.5 py-2 rounded-md text-sm ${
                        currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Map View Toggle */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">View Issues on Map</h3>
                  <p className="text-sm text-gray-600 mt-1">See issues in your area on an interactive map</p>
                </div>
                <div className="flex-shrink-0">
                  <button 
                    onClick={() => navigate('/map')}
                    className="w-full sm:w-auto px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center text-sm font-medium"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    <span>View Map</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default BrowseAreaIssues;